import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { formatFCFA, imageUrl, CLOTHING_TYPES, SIZES } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Pencil, Upload, X, FileUp, Download, Link2, Archive } from "lucide-react";
import { LinkedPicker } from "../../components/admin/LinkedPicker";

const EMPTY = {
  nom: "", description: "", prix: 0, images: [], taille: "",
  disponibilite: "En stock",
  cout_article: 0, marge: 0, cout_livraison: 0,
  conseils_yoyo: "",
  statut: "brouillon", tags: [], type: "robe", etat_piece: "", etat_client: "Excellent état",
  linked_ids: [],
};

const STATUT_LABELS = { brouillon: "Brouillon", actif: "Actif", archive: "Archivé" };
const STATUT_COLORS = { brouillon: "bg-gray-100 text-gray-600", actif: "bg-green-100 text-green-700", archive: "bg-red-100 text-red-600" };

function ProductForm({ open, onClose, product, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef();

  const moveImage = (from, to) => {
    if (from === null || from === to) return;
    const arr = [...form.images];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    set("images", arr);
  };

  useEffect(() => {
    if (product) setForm({ ...EMPTY, ...product });
    else setForm(EMPTY);
  }, [product, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("images", [...form.images, ...urls]);
      toast.success("Image(s) ajoutée(s)");
    } catch {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    const payload = {
      ...form,
      prix: parseInt(form.prix) || 0,
      tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
      linked_ids: form.linked_ids || [],
    };
    try {
      if (product) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produit enregistré");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-[#FBF1EE] [&>*]:min-w-0">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2C1A14]">{product ? "Modifier" : "Nouveau"} produit</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nom *</Label>
            <Input data-testid="product-form-nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="product-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 bg-white" />
          </div>
          <div>
            <Label>Conseils de Yoyo</Label>
            <Textarea data-testid="product-form-conseils" value={form.conseils_yoyo || ""} onChange={(e) => set("conseils_yoyo", e.target.value)} placeholder="Comment porter cette pièce, avec quoi l'associer…" className="mt-1 bg-white" />
          </div>
          <div className="rounded-lg bg-[#F6E4DE] p-4">
            <Label className="text-sm font-semibold text-[#2C1A14]">Tarification</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Coût de l'article</Label>
                <Input type="number" data-testid="product-form-cout" value={form.cout_article || ""} onChange={(e) => set("cout_article", parseInt(e.target.value) || 0)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Marge</Label>
                <Input type="number" data-testid="product-form-marge" value={form.marge || ""} onChange={(e) => set("marge", parseInt(e.target.value) || 0)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Coût livraison (marchand)</Label>
                <Input type="number" data-testid="product-form-cout-livraison" value={form.cout_livraison || ""} onChange={(e) => set("cout_livraison", parseInt(e.target.value) || 0)} className="mt-1 bg-white" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-md bg-white px-4 py-2 ring-1 ring-[#E8D5CE]">
              <span className="text-sm text-[#6E544A]">Prix client (coût + marge)</span>
              <span className="font-serif text-lg font-semibold text-[#D76E4D]" data-testid="product-form-prix-calc">{formatFCFA((parseInt(form.cout_article) || 0) + (parseInt(form.marge) || 0))}</span>
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="product-form-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{CLOTHING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <LinkedPicker selfId={product?.id} value={form.linked_ids || []} onChange={(v) => set("linked_ids", v)} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Statut</Label>
              <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
                <SelectTrigger data-testid="product-form-statut" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="archive">Archivé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Taille (informatif)</Label>
              <Select value={form.taille || "none"} onValueChange={(v) => set("taille", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="product-form-taille" className="mt-1 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non précisée</SelectItem>
                  {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-[#F6E4DE] p-4">
            <div>
              <Label>Disponibilité</Label>
              <p className="text-xs text-[#9B7E73]">Pièce unique : {form.disponibilite === "Rupture" ? "vendue / retirée" : "disponible à la vente"}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${form.disponibilite === "Rupture" ? "text-red-600" : "text-green-700"}`}>{form.disponibilite}</span>
              <Switch
                checked={form.disponibilite !== "Rupture"}
                onCheckedChange={(v) => set("disponibilite", v ? "En stock" : "Rupture")}
                data-testid="product-form-disponibilite"
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-[#F6E4DE] p-4">
            <div>
              <Label>Nouveauté</Label>
              <p className="text-xs text-[#9B7E73]">Afficher cette pièce dans la page « Nouveautés »</p>
            </div>
            <Switch
              checked={(Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map((t) => t.trim())).includes("nouveau")}
              onCheckedChange={(v) => {
                const arr = Array.isArray(form.tags) ? [...form.tags] : String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
                const s = new Set(arr);
                if (v) s.add("nouveau"); else s.delete("nouveau");
                set("tags", Array.from(s));
              }}
              data-testid="product-form-nouveaute"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>État interne (admin)</Label>
              <Input data-testid="product-form-etat-piece" value={form.etat_piece} onChange={(e) => set("etat_piece", e.target.value)} placeholder="Seconde main remise à neuf / Neuf" className="mt-1 bg-white" />
            </div>
            <div>
              <Label>État affiché (client)</Label>
              <Select value={form.etat_client || "Excellent état"} onValueChange={(v) => set("etat_client", v)}>
                <SelectTrigger data-testid="product-form-etat-client" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Neuf", "Comme neuf", "Excellent état", "Très bon état", "Bon état", "État correct"].map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Tags (séparés par des virgules)</Label>
            <Input data-testid="product-form-tags" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="nouveau, coup-de-coeur" className="mt-1 bg-white" />
          </div>

          <div>
            <Label>Images</Label>
            <p className="text-xs text-[#9B7E73]">Glissez-déposez les photos pour changer leur ordre. La première est la photo principale.</p>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((img, i) => (
                <div
                  key={img || i}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { moveImage(dragIdx, i); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                  data-testid={`form-image-${i}`}
                  className={`relative h-24 w-20 cursor-move overflow-hidden rounded-lg ring-2 transition-opacity ${dragIdx === i ? "opacity-40 ring-[#D76E4D]" : i === 0 ? "ring-[#2C1A14]" : "ring-transparent"}`}
                >
                  <img src={imageUrl(img, 320)} alt="" draggable={false} className="h-full w-full object-cover" />
                  {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-[#2C1A14]/80 text-center text-[9px] font-semibold uppercase text-white">Principale</span>}
                  <div className="absolute left-1 top-1 flex gap-0.5">
                    {i > 0 && <button type="button" onClick={() => moveImage(i, i - 1)} data-testid={`form-image-left-${i}`} className="rounded-full bg-black/60 px-1 text-[10px] text-white">‹</button>}
                    {i < form.images.length - 1 && <button type="button" onClick={() => moveImage(i, i + 1)} data-testid={`form-image-right-${i}`} className="rounded-full bg-black/60 px-1 text-[10px] text-white">›</button>}
                  </div>
                  <button type="button" onClick={() => set("images", form.images.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-image-button"
                className="flex h-24 w-20 flex-col items-center justify-center rounded-lg border-2 border-dashed border-[#D76E4D] text-[#D76E4D]">
                <Upload className="h-5 w-5" />
                <span className="mt-1 text-[10px]">{uploading ? "..." : "Ajouter"}</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E8D5CE]">Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="product-form-save" className="bg-[#D76E4D] text-white hover:bg-[#C05A3A]">{saving ? "..." : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState("all");
  const [type, setType] = useState("all");
  const [dispo, setDispo] = useState("all");
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkStatut, setBulkStatut] = useState("actif");

  const load = () => {
    api.get("/admin/products", { params: {
      page, limit: 20,
      ...(search ? { search } : {}),
      ...(statut !== "all" ? { statut } : {}),
      ...(type !== "all" ? { type } : {}),
      ...(dispo !== "all" ? { disponibilite: dispo } : {}),
    }}).then((r) => { setItems(r.data.items); setTotal(r.data.total); setPages(r.data.pages); });
  };

  useEffect(() => { load(); }, [page, statut, type, dispo]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [search]);

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const duplicate = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast.success("Produit dupliqué"); load(); };
  const remove = async (id) => { if (!window.confirm("Supprimer ce produit ?")) return; await api.delete(`/admin/products/${id}`); toast.success("Supprimé"); load(); };
  const toggleNouveaute = async (p) => {
    const isNew = (p.tags || []).includes("nouveau");
    await api.put(`/admin/products/${p.id}/nouveaute`, { nouveau: !isNew });
    toast.success(!isNew ? "Ajouté aux Nouveautés" : "Retiré des Nouveautés");
    load();
  };
  const applyBulk = async () => {
    if (!selected.length) { toast.error("Sélectionnez des produits"); return; }
    await api.post("/admin/products/bulk-status", { ids: selected, statut: bulkStatut });
    toast.success(`${selected.length} produit(s) mis à jour`);
    setSelected([]); load();
  };

  const archiveSold = async () => {
    if (!window.confirm("Archiver toutes les pièces vendues (en rupture) ? Elles disparaîtront de la boutique.")) return;
    const { data } = await api.post("/admin/products/archive-sold");
    toast.success(`${data.count} pièce(s) vendue(s) archivée(s)`);
    load();
  };
  const savePosition = async (p, value) => {
    const pos = parseInt(value) || 0;
    if (pos === (p.position || 0)) return;
    await api.put(`/admin/products/${p.id}/position`, { position: pos });
    toast.success("Ordre d'affichage mis à jour");
    load();
  };
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef();

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} produit(s) importé(s) sur ${data.total_rows} ligne(s)`);
      setImportOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import échoué");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/admin/products/import-template", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = "modele_import_produits.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-medium text-[#2C1A14]">Produits <span className="text-base text-[#9B7E73]">({total})</span></h1>
        <div className="flex flex-wrap gap-2">
          <Button onClick={archiveSold} data-testid="archive-sold-button" variant="outline" className="rounded-full border-[#2C1A14] text-[#2C1A14] hover:bg-[#F6E4DE]"><Archive className="mr-1 h-4 w-4" /> Archiver les vendues</Button>
          <Button onClick={() => setImportOpen(true)} data-testid="import-button" variant="outline" className="rounded-full border-[#D76E4D] text-[#D76E4D] hover:bg-[#F6E4DE]"><FileUp className="mr-1 h-4 w-4" /> Import CSV/Excel</Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-product-button" className="rounded-full bg-[#D76E4D] text-white hover:bg-[#C05A3A]"><Plus className="mr-1 h-4 w-4" /> Nouveau produit</Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search" className="max-w-xs bg-white" />
        <Select value={statut} onValueChange={setStatut}>
          <SelectTrigger data-testid="filter-statut" className="w-40 bg-white"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="brouillon">Brouillon</SelectItem>
            <SelectItem value="actif">Actif</SelectItem>
            <SelectItem value="archive">Archivé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-40 bg-white"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {CLOTHING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dispo} onValueChange={setDispo}>
          <SelectTrigger data-testid="filter-disponibilite" className="w-44 bg-white"><SelectValue placeholder="Disponibilité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes disponibilités</SelectItem>
            <SelectItem value="En stock">En stock</SelectItem>
            <SelectItem value="Rupture">Rupture</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.length > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-[#F6E4DE] p-3">
          <span className="text-sm font-medium text-[#2C1A14]">{selected.length} sélectionné(s)</span>
          <Select value={bulkStatut} onValueChange={setBulkStatut}>
            <SelectTrigger className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="actif">Publier (Actif)</SelectItem>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="archive">Archiver</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={applyBulk} data-testid="apply-bulk-status" className="bg-[#2C1A14] text-white">Appliquer</Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-[#E8D5CE]">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-[#E8D5CE] bg-[#FBF1EE] text-left text-xs uppercase text-[#9B7E73]">
            <tr>
              <th className="p-3 w-10"></th>
              <th className="p-3">Produit</th>
              <th className="hidden p-3 sm:table-cell">Type</th>
              <th className="p-3">Prix</th>
              <th className="hidden p-3 md:table-cell">Statut</th>
              <th className="hidden p-3 text-center lg:table-cell">Nouveauté</th>
              <th className="p-3 text-center" title="Plus le nombre est grand, plus la pièce apparaît tôt dans la boutique">Ordre</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-[#F0E4DE] last:border-0">
                <td className="p-3"><Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-${p.id}`} /></td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={imageUrl(p.images?.[0], 320)} alt="" className="h-12 w-10 rounded object-cover" />
                    <span className="font-medium text-[#2C1A14]">{p.nom}</span>
                    {(p.linked_ids || []).length > 0 && (
                      <span title="Pièces reliées" data-testid={`linked-count-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#F6E4DE] px-2 py-0.5 text-xs text-[#D76E4D]">
                        <Link2 className="h-3 w-3" />{p.linked_ids.length}
                      </span>
                    )}
                  </div>
                </td>
                <td className="hidden p-3 capitalize text-[#6E544A] sm:table-cell">{p.type}</td>
                <td className="p-3 text-[#D76E4D]">{formatFCFA(p.prix)}<span className="ml-1 hidden text-xs text-green-600 lg:inline">(+{formatFCFA(p.marge || 0)})</span></td>
                <td className="hidden p-3 md:table-cell"><Badge className={`${STATUT_COLORS[p.statut]} border-0`}>{STATUT_LABELS[p.statut]}</Badge></td>
                <td className="hidden p-3 text-center lg:table-cell">
                  <Switch
                    checked={(p.tags || []).includes("nouveau")}
                    onCheckedChange={() => toggleNouveaute(p)}
                    data-testid={`nouveaute-toggle-${p.id}`}
                  />
                </td>
                <td className="p-3 text-center">
                  <Input
                    type="number"
                    defaultValue={p.position || 0}
                    key={`${p.id}-${p.position || 0}`}
                    onBlur={(e) => savePosition(p, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    data-testid={`position-input-${p.id}`}
                    className="mx-auto h-8 w-16 bg-white text-center"
                  />
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`edit-${p.id}`} className="rounded p-2 text-[#6E544A] hover:bg-[#F6E4DE]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => duplicate(p.id)} data-testid={`duplicate-${p.id}`} className="rounded p-2 text-[#6E544A] hover:bg-[#F6E4DE]"><Copy className="h-4 w-4" /></button>
                    <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`} className="rounded p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#9B7E73]">Aucun produit</td></tr>}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-[#E8D5CE]">Précédent</Button>
          <span className="text-sm text-[#6E544A]">Page {page} / {pages}</span>
          <Button variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="border-[#E8D5CE]">Suivant</Button>
        </div>
      )}

      <ProductForm open={dialogOpen} onClose={() => setDialogOpen(false)} product={editing} onSaved={load} />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg bg-[#FBF1EE]">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2C1A14]">Import de masse</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-[#6E544A]">
              Importez plusieurs articles d'un coup depuis un fichier <strong>CSV</strong> ou <strong>Excel (.xlsx)</strong>.
              Colonnes attendues : <strong>nom</strong>, <strong>prix</strong>, <strong>taille</strong>, <strong>type</strong>, <strong>description</strong>, <strong>conseils de yoyo</strong>.
            </p>
            <Button onClick={downloadTemplate} data-testid="download-template" variant="outline" className="w-full border-[#E8D5CE]">
              <Download className="mr-2 h-4 w-4" /> Télécharger le modèle CSV
            </Button>
            <button
              onClick={() => importRef.current?.click()}
              data-testid="import-dropzone"
              className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#D76E4D] bg-white p-8 text-[#D76E4D] hover:bg-[#F6E4DE]"
            >
              <FileUp className="h-8 w-8" />
              <span className="mt-2 text-sm font-medium">{importing ? "Import en cours…" : "Choisir un fichier CSV / Excel"}</span>
            </button>
            <input ref={importRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={doImport} data-testid="import-file-input" />
            <p className="text-xs text-[#9B7E73]">Les articles importés sont créés en statut « Actif », « En stock », prix = coût (marge 0, à ajuster ensuite).</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
